import React, { forwardRef, useEffect, useImperativeHandle, useRef, useState } from 'react'
import { Button } from '~/components/common/Button'
import { LandingChatPlusMenu } from '~/components/chat/LandingChatPlusMenu'
import { UploadCardsContainer } from '~/components/chat/UploadCardsContainer'
import { ChatBox } from '~/components/chat/ChatBox'
import { CHAT_SLASH_COMMANDS } from '~/components/chat/slashCommands'
import { useModal } from '~/components/modals/CurieModal'
import { Typography } from '~/components/common/Typography'
import { TabItem } from '~/components/common/TabItem'

import { useDispatch, useSelector } from 'react-redux'
import { useNavigate, useParams } from 'react-router-dom'
import { CommandType } from '~/constants/commands'
import { useConversationsContext } from '~/contexts/ConversationContext'
import { useContextUpload } from '~/hooks/useContextUpload'
import { workspaceChatStart } from '~/services/chatService'
import { speechToText } from '~/services/speechService'
import {
  completeRepositorySelection,
  resetFlow,
  selectCurrentStep,
  selectFlowRepository,
  selectIntegrationTestFlow,
  startFlow
} from '~/store/integrationTestFlowSlice'
import { RepositoryBranch } from '~/types/Conversations'
import { getCommandUrlName } from '~/utils/urlUtils'

import type { ComponentProps } from 'react'
import { useCommandPermissions } from '~/components/chat/gallery-screen/commandPermissions'
import { useUserSpecificConfig } from '~/contexts/AppConfigContext'
import IntegrationRepoSelectionModal from '~/components/task-forms/shared/IntegrationRepoSelectionModal'
import { RepoSelectModal } from '~/components/repo-selection/RepoSelectModal'
import type { UploadedRepo } from '~/components/repo-selection/types'
import { useCompactViewport } from '~/hooks/useCompactViewport'
import { useTypewriterPlaceholder } from '~/hooks/useTypewriterPlaceholder'
import { bridge } from '~/services/bridge'
import { uploadFile } from '~/services/organizationService'
import { extractFileDisplayName } from '~/utils/repositoryUtils'
import { BetterToast } from '~/utils/toast'
import { SkillsView } from './SkillsView'

type Skill = ComponentProps<typeof SkillsView>['skills'][number]

function MigrationGeneratorIcon() {
  return (
    <svg width='24' height='24' viewBox='0 0 20 18' fill='none' xmlns='http://www.w3.org/2000/svg'>
      <path
        d='M5 13V1M5 1L1 5M5 1L9 5M15 5V17M15 17L19 13M15 17L11 13'
        stroke='currentColor'
        strokeOpacity='0.88'
        strokeWidth='1.5'
        strokeLinecap='round'
        strokeLinejoin='round'
      />
    </svg>
  )
}

function BillableMuleFlowsIcon() {
  return (
    <svg width='24' height='24' viewBox='0 0 24 24' fill='none' xmlns='http://www.w3.org/2000/svg'>
      <path d='M4 7H20H4Z' fill='currentColor' fillOpacity='0.45' />
      <path
        d='M4 7H20'
        stroke='currentColor'
        strokeOpacity='0.88'
        strokeWidth='1.5'
        strokeLinecap='round'
        strokeLinejoin='round'
      />
      <path
        d='M4 17H9'
        stroke='currentColor'
        strokeOpacity='0.88'
        strokeWidth='1.5'
        strokeLinecap='round'
        strokeLinejoin='round'
      />
      <path
        d='M4 12H17.5C18.8807 12 20 13.1193 20 14.5C20 15.8807 18.8807 17 17.5 17H12.5'
        stroke='currentColor'
        strokeOpacity='0.88'
        strokeWidth='1.5'
        strokeLinecap='round'
        strokeLinejoin='round'
      />
      <path
        d='M15 15.5L12.5 17L15 18.5V15.5Z'
        stroke='currentColor'
        strokeOpacity='0.88'
        strokeWidth='1.5'
        strokeLinecap='round'
        strokeLinejoin='round'
      />
    </svg>
  )
}

function MigrationAcceleratorIcon() {
  return (
    <svg width='24' height='24' viewBox='0 0 24 24' fill='none' xmlns='http://www.w3.org/2000/svg'>
      <path
        fillRule='evenodd'
        clipRule='evenodd'
        d='M20.95 9.11C21.6908 9.10827 22.3718 9.51614 22.72 10.17C23.0816 10.8105 23.0624 11.5978 22.67 12.22L18.1 19.07C17.6249 19.8375 16.6947 20.1916 15.8295 19.9344C14.9643 19.6772 14.3786 18.8724 14.4 17.97V15.12H12.26C11.5441 15.1228 10.8812 14.7428 10.522 14.1235C10.1628 13.5041 10.162 12.74 10.52 12.12L15.16 4C15.5169 3.38183 16.1762 3.00073 16.89 3C17.9946 3 18.89 3.89543 18.89 5V9.11H20.95ZM16.8 18.24L21.37 11.39C21.486 11.2356 21.5028 11.0282 21.413 10.8572C21.3232 10.6862 21.143 10.5822 20.95 10.59H18.39C17.8377 10.59 17.39 10.1423 17.39 9.59V4.98C17.39 4.84564 17.336 4.71692 17.24 4.62286C17.1441 4.5288 17.0143 4.47731 16.88 4.48C16.6969 4.48062 16.5301 4.58535 16.45 4.75L11.81 12.87C11.7251 13.0251 11.7285 13.2135 11.8189 13.3655C11.9092 13.5175 12.0732 13.6105 12.25 13.61H14.89C15.4423 13.61 15.89 14.0577 15.89 14.61V17.96C15.8927 18.1772 16.0353 18.3679 16.2429 18.4318C16.4506 18.4956 16.6757 18.4182 16.8 18.24Z'
        fill='currentColor'
      />
      <path
        d='M8.75 5.85H3.95C3.53579 5.85 3.2 6.18579 3.2 6.6C3.2 7.01421 3.53579 7.35 3.95 7.35H8.75C9.16421 7.35 9.5 7.01421 9.5 6.6C9.5 6.18579 9.16421 5.85 8.75 5.85Z'
        fill='currentColor'
      />
      <path
        d='M8.3 11.33C8.29461 10.918 7.96196 10.5854 7.55 10.58H2.75C2.33579 10.58 2 10.9158 2 11.33C2 11.7442 2.33579 12.08 2.75 12.08H7.55C7.74891 12.08 7.93968 12.001 8.08033 11.8603C8.22098 11.7197 8.3 11.5289 8.3 11.33Z'
        fill='currentColor'
      />
      <path
        d='M8.75 16.35H3.95C3.53579 16.35 3.2 16.6858 3.2 17.1C3.2 17.5142 3.53579 17.85 3.95 17.85H8.75C9.16421 17.85 9.5 17.5142 9.5 17.1C9.5 16.6858 9.16421 16.35 8.75 16.35Z'
        fill='currentColor'
      />
    </svg>
  )
}

function AzureIcon() {
  // Mock of the Azure "A" logo, monochrome to match the other skill icons.
  return (
    <svg width='24' height='24' viewBox='0 0 18 18' fill='none' xmlns='http://www.w3.org/2000/svg'>
      <path
        d='M8.18 2.3h3.06L8.06 11.9a.69.69 0 0 1-.65.47H4.9a.69.69 0 0 1-.65-.92L7.53 2.77a.69.69 0 0 1 .65-.47z'
        fill='currentColor'
      />
      <path
        d='M11.4 13.06H6.27a.33.33 0 0 0-.22.57l3.27 3.05a.69.69 0 0 0 .47.19h2.86z'
        fill='currentColor'
        fillOpacity='0.6'
      />
      <path
        d='M11.75 2.77a.69.69 0 0 0-.65-.47H8.2c.29 0 .55.18.65.47l3.26 9.65.49 1.45a.69.69 0 0 1-.65.92h3.03a.69.69 0 0 0 .65-.92z'
        fill='currentColor'
      />
    </svg>
  )
}

function MuleRefactorAssistantIcon() {
  return (
    <svg width='24' height='24' viewBox='0 0 24 24' fill='none' xmlns='http://www.w3.org/2000/svg'>
      <path
        d='M2 18V6C2 4.34315 3.34315 3 5 3H7C8.65685 3 10 4.34315 10 6V18C10 19.6569 8.65685 21 7 21H5C3.34315 21 2 19.6569 2 18Z'
        stroke='currentColor'
        strokeOpacity='0.88'
        strokeWidth='1.5'
      />
      <path
        d='M16 3H18C20.2091 3 22 4.79086 22 7V17C22 19.2091 20.2091 21 18 21H16'
        stroke='currentColor'
        strokeOpacity='0.88'
        strokeWidth='1.5'
        strokeLinecap='round'
        strokeLinejoin='round'
      />
      <path
        d='M18 12L15 15M10 12H18H10ZM18 12L15 9L18 12Z'
        stroke='currentColor'
        strokeOpacity='0.88'
        strokeWidth='1.5'
        strokeLinecap='round'
        strokeLinejoin='round'
      />
    </svg>
  )
}

function MuleVersionUpgradeIcon() {
  return (
    <svg width='24' height='24' viewBox='0 0 24 24' fill='none' xmlns='http://www.w3.org/2000/svg'>
      <path
        d='M19.846 0L21.923 2.077M19.846 5.538V0V5.538ZM19.846 0L17.769 2.077L19.846 0Z'
        stroke='currentColor'
        strokeOpacity='0.88'
        strokeWidth='1.2'
        strokeLinecap='round'
        strokeLinejoin='round'
      />
      <g clipPath='url(#clip0_mule_upgrade)'>
        <path
          d='M9.531 5.063C9.924 3.44 12.076 3.44 12.469 5.063C12.5286 5.3084 12.6447 5.53587 12.8083 5.7292C12.972 5.92253 13.1784 6.07679 13.4116 6.17981C13.6448 6.28282 13.8986 6.3319 14.1541 6.31298C14.4096 6.29406 14.6572 6.21771 14.8784 6.09C16.3008 5.2176 17.9328 6.8488 17.0608 8.2712C16.9331 8.49219 16.8568 8.73968 16.8378 8.99497C16.8188 9.25027 16.8575 9.50677 16.9512 9.74474C17.0449 9.98271 17.1913 10.1963 17.3797 10.3698C17.568 10.5433 17.7935 10.6723 18.0384 10.7472C19.661 11.1392 19.661 13.291 18.0384 13.683C17.7929 13.7426 17.5655 13.8587 17.3721 14.0223C17.1788 14.186 17.0245 14.3924 16.9215 14.6256C16.8185 14.8588 16.7694 15.1126 16.7883 15.3681C16.8072 15.6236 16.8836 15.8712 17.0112 16.0924C17.8836 17.5148 16.2524 19.1468 14.8296 18.2748C14.6086 18.1471 14.3611 18.0708 14.1058 18.0518C13.8505 18.0328 13.594 18.0715 13.3561 18.1652C13.1181 18.2589 12.9045 18.4053 12.731 18.5937C12.5575 18.782 12.4285 19.0075 12.3536 19.2524C11.9616 20.875 9.8096 20.875 9.4176 19.2524C9.35804 19.0069 9.24193 18.7795 9.07829 18.5861C8.91465 18.3928 8.70824 18.2385 8.47502 18.1355C8.2418 18.0325 7.98803 17.9834 7.73256 18.0023C7.47709 18.0212 7.2295 18.0976 7.0084 18.2252C5.586 19.0976 3.954 17.4664 4.826 16.044C4.95367 15.823 5.03001 15.5755 5.04894 15.3202C5.06787 15.0649 5.02917 14.8084 4.93547 14.5705C4.84176 14.3325 4.69533 14.119 4.50698 13.9454C4.31863 13.7719 4.09311 13.643 3.8484 13.568C2.226 13.176 2.226 11.024 3.8484 10.632C4.09395 10.5724 4.32142 10.4563 4.51475 10.2927C4.70808 10.129 4.86234 9.92259 4.96536 9.68936C5.06837 9.45614 5.11745 9.20236 5.09853 8.94689C5.07961 8.69142 5.00326 8.44383 4.8756 8.2228C4.0032 6.8004 5.6344 5.1684 7.0572 6.0404C8.0056 6.6116 9.2256 6.1172 9.4776 5.0252L9.531 5.063Z'
          stroke='currentColor'
          strokeOpacity='0.88'
          strokeWidth='1.3'
          strokeLinecap='round'
          strokeLinejoin='round'
        />
        <path
          d='M11.077 15.385C12.589 15.385 13.8155 14.1585 13.8155 12.6465C13.8155 11.1345 12.589 9.908 11.077 9.908C9.565 9.908 8.3385 11.1345 8.3385 12.6465C8.3385 14.1585 9.565 15.385 11.077 15.385Z'
          stroke='currentColor'
          strokeOpacity='0.88'
          strokeWidth='1.3'
          strokeLinecap='round'
          strokeLinejoin='round'
        />
      </g>
      <defs>
        <clipPath id='clip0_mule_upgrade'>
          <rect width='22.154' height='22.154' fill='white' transform='translate(0 1.538)' />
        </clipPath>
      </defs>
    </svg>
  )
}

function SpringIntegrationRefactorIcon() {
  return (
    <svg width='24' height='24' viewBox='0 0 24 24' fill='none' xmlns='http://www.w3.org/2000/svg'>
      <path
        d='M4 6H8M4 12H20M4 18H8'
        stroke='currentColor'
        strokeOpacity='0.88'
        strokeWidth='1.5'
        strokeLinecap='round'
        strokeLinejoin='round'
      />
      <path
        d='M12 6L20 6'
        stroke='currentColor'
        strokeOpacity='0.88'
        strokeWidth='1.5'
        strokeLinecap='round'
        strokeLinejoin='round'
      />
      <path
        d='M12 18L20 18'
        stroke='currentColor'
        strokeOpacity='0.88'
        strokeWidth='1.5'
        strokeLinecap='round'
        strokeLinejoin='round'
      />
      <circle cx='10' cy='6' r='1.5' stroke='currentColor' strokeOpacity='0.88' strokeWidth='1.2' />
      <circle
        cx='10'
        cy='18'
        r='1.5'
        stroke='currentColor'
        strokeOpacity='0.88'
        strokeWidth='1.2'
      />
    </svg>
  )
}

// Inline SVG components
const ChevronDownIcon = () => (
  <svg width='16' height='16' viewBox='0 0 16 16' fill='none' xmlns='http://www.w3.org/2000/svg'>
    <path
      d='M4 6L8 10L12 6'
      stroke='currentColor'
      strokeWidth='1.5'
      strokeLinecap='round'
      strokeLinejoin='round'
    />
  </svg>
)

const CodeIcon = ({ className }: { className?: string }) => (
  <svg
    width='16'
    height='16'
    viewBox='0 0 16 16'
    fill='none'
    xmlns='http://www.w3.org/2000/svg'
    className={className}
  >
    <path d='M8.99984 4L6.6665 12.3333L8.99984 4Z' fill='currentColor' fillOpacity='0.65' />
    <path
      d='M8.99984 4L6.6665 12.3333'
      stroke='currentColor'
      strokeOpacity='0.45'
      strokeWidth='1.5'
      strokeLinecap='round'
      strokeLinejoin='round'
    />
    <path
      d='M4.33333 5.6665L2 7.99984L4.33333 10.3332'
      stroke='currentColor'
      strokeOpacity='0.45'
      strokeWidth='1.5'
      strokeLinecap='round'
      strokeLinejoin='round'
    />
    <path
      d='M11.6665 5.6665L13.9998 7.99984L11.6665 10.3332'
      stroke='currentColor'
      strokeOpacity='0.45'
      strokeWidth='1.5'
      strokeLinecap='round'
      strokeLinejoin='round'
    />
  </svg>
)

export const CoderIcon = ({ className }: { className?: string }) => (
  <svg
    xmlns='http://www.w3.org/2000/svg'
    width='24'
    height='24'
    viewBox='0 0 18 18'
    fill='none'
    className={className}
  >
    <path
      d='M10.125 4.5L7.5 13.875'
      stroke='#8E8E8E'
      strokeWidth='1.5'
      strokeLinecap='round'
      strokeLinejoin='round'
    />
    <path
      d='M4.875 6.375L2.25 9L4.875 11.625'
      stroke='#8E8E8E'
      strokeWidth='1.5'
      strokeLinecap='round'
      strokeLinejoin='round'
    />
    <path
      d='M13.125 6.375L15.75 9L13.125 11.625'
      stroke='#8E8E8E'
      strokeWidth='1.5'
      strokeLinecap='round'
      strokeLinejoin='round'
    />
  </svg>
)

const TestIcon = ({ className }: { className?: string }) => (
  <svg
    width='16'
    height='16'
    viewBox='0 0 16 16'
    fill='none'
    xmlns='http://www.w3.org/2000/svg'
    className={className}
  >
    <path
      d='M13.8556 12.4856L10 6.0575V2.5H10.5C10.6326 2.5 10.7598 2.44732 10.8536 2.35355C10.9473 2.25979 11 2.13261 11 2C11 1.86739 10.9473 1.74021 10.8536 1.64645C10.7598 1.55268 10.6326 1.5 10.5 1.5H5.50001C5.36741 1.5 5.24023 1.55268 5.14646 1.64645C5.05269 1.74021 5.00001 1.86739 5.00001 2C5.00001 2.13261 5.05269 2.25979 5.14646 2.35355C5.24023 2.44732 5.36741 2.5 5.50001 2.5H6.00001V6.0575L2.14439 12.4856C2.05345 12.6372 2.00434 12.8102 2.00204 12.987C1.99974 13.1638 2.04435 13.338 2.13131 13.4919C2.21827 13.6458 2.34449 13.7739 2.49709 13.8632C2.64969 13.9525 2.82323 13.9997 3.00001 14H13C13.177 14 13.3507 13.953 13.5036 13.8639C13.6565 13.7748 13.7829 13.6467 13.8701 13.4927C13.9573 13.3388 14.0021 13.1644 13.9999 12.9875C13.9977 12.8105 13.9485 12.6374 13.8575 12.4856H13.8556ZM6.92876 6.45312C6.97553 6.37561 7.00017 6.28678 7.00001 6.19625V2.5H9.00001V6.19625C8.99986 6.28678 9.0245 6.37561 9.07126 6.45312L11.46 10.4375C10.71 10.5856 9.64314 10.5231 8.22564 9.80562C7.23126 9.3025 6.28501 9.03562 5.39939 9.005L6.92876 6.45312ZM3.00001 13L4.78376 10.0262C5.67439 9.9175 6.67814 10.1419 7.77251 10.6962C8.96001 11.2969 9.96001 11.5013 10.7725 11.5013C11.1857 11.5031 11.5971 11.4463 11.9944 11.3325L13 13H3.00001Z'
      fill='currentColor'
      fillOpacity='0.65'
    />
  </svg>
)

const DocumentIcon = ({ className }: { className?: string }) => (
  <svg
    width='16'
    height='16'
    viewBox='0 0 16 16'
    fill='none'
    xmlns='http://www.w3.org/2000/svg'
    className={className}
  >
    <path
      d='M13.3538 5.14625L9.85375 1.64625C9.80728 1.59983 9.75212 1.56303 9.69143 1.53793C9.63073 1.51284 9.56568 1.49995 9.5 1.5H3.5C3.23478 1.5 2.98043 1.60536 2.79289 1.79289C2.60536 1.98043 2.5 2.23478 2.5 2.5V13.5C2.5 13.7652 2.60536 14.0196 2.79289 14.2071C2.98043 14.3946 3.23478 14.5 3.5 14.5H12.5C12.7652 14.5 13.0196 14.3946 13.2071 14.2071C13.3946 14.0196 13.5 13.7652 13.5 13.5V5.5C13.5001 5.43432 13.4872 5.36927 13.4621 5.30858C13.437 5.24788 13.4002 5.19272 13.3538 5.14625ZM10 3.20688L11.7931 5H10V3.20688ZM12.5 13.5H3.5V2.5H9V5.5C9 5.63261 9.05268 5.75979 9.14645 5.85355C9.24021 5.94732 9.36739 6 9.5 6H12.5V13.5ZM10.5 8.5C10.5 8.63261 10.4473 8.75979 10.3536 8.85355C10.2598 8.94732 10.1326 9 10 9H6C5.86739 9 5.74021 8.94732 5.64645 8.85355C5.55268 8.75979 5.5 8.63261 5.5 8.5C5.5 8.36739 5.55268 8.24021 5.64645 8.14645C5.74021 8.05268 5.86739 8 6 8H10C10.1326 8 10.2598 8.05268 10.3536 8.14645C10.4473 8.24021 10.5 8.36739 10.5 8.5ZM10.5 10.5C10.5 10.6326 10.4473 10.7598 10.3536 10.8536C10.2598 10.9473 10.1326 11 10 11H6C5.86739 11 5.74021 10.9473 5.64645 10.8536C5.55268 10.7598 5.5 10.6326 5.5 10.5C5.5 10.3674 5.55268 10.2402 5.64645 10.1464C5.74021 10.0527 5.86739 10 6 10H10C10.1326 10 10.2598 10.0527 10.3536 10.1464C10.4473 10.2402 10.5 10.3674 10.5 10.5Z'
      fill='currentColor'
      fillOpacity='0.65'
    />
  </svg>
)

export const DataWeaveIcon = ({ className }: { className?: string }) => (
  <svg
    width='24'
    height='24'
    viewBox='0 0 24 24'
    fill='none'
    xmlns='http://www.w3.org/2000/svg'
    className={className}
  >
    <path
      d='M12 2.25C6.95344 2.25 3 4.55625 3 7.5V16.5C3 19.4438 6.95344 21.75 12 21.75C17.0466 21.75 21 19.4438 21 16.5V7.5C21 4.55625 17.0466 2.25 12 2.25ZM19.5 12C19.5 12.9019 18.7612 13.8216 17.4741 14.5238C16.0247 15.3141 14.0803 15.75 12 15.75C9.91969 15.75 7.97531 15.3141 6.52594 14.5238C5.23875 13.8216 4.5 12.9019 4.5 12V10.44C6.09938 11.8463 8.83406 12.75 12 12.75C15.1659 12.75 17.9006 11.8425 19.5 10.44V12ZM6.52594 4.97625C7.97531 4.18594 9.91969 3.75 12 3.75C14.0803 3.75 16.0247 4.18594 17.4741 4.97625C18.7612 5.67844 19.5 6.59812 19.5 7.5C19.5 8.40188 18.7612 9.32156 17.4741 10.0237C16.0247 10.8141 14.0803 11.25 12 11.25C9.91969 11.25 7.97531 10.8141 6.52594 10.0237C5.23875 9.32156 4.5 8.40188 4.5 7.5C4.5 6.59812 5.23875 5.67844 6.52594 4.97625ZM17.4741 19.0238C16.0247 19.8141 14.0803 20.25 12 20.25C9.91969 20.25 7.97531 19.8141 6.52594 19.0238C5.23875 18.3216 4.5 17.4019 4.5 16.5V14.94C6.09938 16.3463 8.83406 17.25 12 17.25C15.1659 17.25 17.9006 16.3425 19.5 14.94V16.5C19.5 17.4019 18.7612 18.3216 17.4741 19.0238Z'
      fill='currentColor'
      fillOpacity='0.45'
    />
  </svg>
)

const MigrationIcon = () => (
  <svg width='16' height='16' viewBox='0 0 16 16' fill='none' xmlns='http://www.w3.org/2000/svg'>
    <path
      fillRule='evenodd'
      clipRule='evenodd'
      d='M9.14645 1.14645C9.34171 0.951184 9.65829 0.951184 9.85355 1.14645L13.3536 4.64645C13.5488 4.84171 13.5488 5.15829 13.3536 5.35355L9.85355 8.85355C9.65829 9.04882 9.34171 9.04882 9.14645 8.85355C8.95118 8.65829 8.95118 8.34171 9.14645 8.14645L12.2929 5L9.14645 1.85355C8.95118 1.65829 8.95118 1.34171 9.14645 1.14645Z'
      fill='currentColor'
      fillOpacity='0.88'
    />
    <path
      fillRule='evenodd'
      clipRule='evenodd'
      d='M2.5 5C2.5 4.72386 2.72386 4.5 3 4.5H12.4647C12.7408 4.5 12.9647 4.72386 12.9647 5C12.9647 5.27614 12.7408 5.5 12.4647 5.5H3C2.72386 5.5 2.5 5.27614 2.5 5Z'
      fill='currentColor'
      fillOpacity='0.88'
    />
    <path
      fillRule='evenodd'
      clipRule='evenodd'
      d='M6.85355 7.14645C7.04882 7.34171 7.04882 7.65829 6.85355 7.85355L3.70711 11L6.85355 14.1464C7.04882 14.3417 7.04882 14.6583 6.85355 14.8536C6.65829 15.0488 6.34171 15.0488 6.14645 14.8536L2.64645 11.3536C2.45118 11.1583 2.45118 10.8417 2.64645 10.6464L6.14645 7.14645C6.34171 6.95118 6.65829 6.95118 6.85355 7.14645Z'
      fill='currentColor'
      fillOpacity='0.88'
    />
    <path
      fillRule='evenodd'
      clipRule='evenodd'
      d='M3.0625 11C3.0625 10.7239 3.28636 10.5 3.5625 10.5H13C13.2761 10.5 13.5 10.7239 13.5 11C13.5 11.2761 13.2761 11.5 13 11.5H3.5625C3.28636 11.5 3.0625 11.2761 3.0625 11Z'
      fill='currentColor'
      fillOpacity='0.88'
    />
  </svg>
)

// AttachUploadButton is now imported from ~/components/chat/AttachUploadButton

// UploadCard and UploadCardsContainer are now imported from ~/components/chat/

type RepoSelectButtonHandle = {
  openModal: () => void
}

interface RepoSelectButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  selectedRepos: RepositoryBranch[]
  setSelectedRepos: (repos: RepositoryBranch[]) => void
  uploadedRepos: UploadedRepo[]
  setUploadedRepos: React.Dispatch<React.SetStateAction<UploadedRepo[]>>
  /** Landing chat: hide full-width trigger until user has a selection; open modal via ref / plus menu. */
  landingToolbarLayout?: boolean
}

/**
 * Trigger button for the chatbox repository picker. The actual picker UI is
 * the canonical `RepoSelectModal`; this component owns only the trigger
 * presentation (label, clear-X, hide-while-empty) and the open/close handle
 * exposed to the landing-chat plus menu via `useImperativeHandle`.
 */
const RepoSelectButton = forwardRef<RepoSelectButtonHandle, RepoSelectButtonProps>(
  function RepoSelectButton(
    {
      className = '',
      selectedRepos,
      setSelectedRepos,
      uploadedRepos,
      setUploadedRepos,
      landingToolbarLayout = false,
      onClick,
      ...rest
    },
    ref
  ) {
    const { isOpen: isModalOpen, open: openModal, close: closeModal } = useModal()
    useImperativeHandle(ref, () => ({ openModal }), [openModal])

    const { workspaceUuid } = useParams<{ workspaceUuid: string }>()
    const isPlugin = bridge.isInPlugin()
    const isCompactViewport = useCompactViewport()
    const usePluginOptimizedLayout = isPlugin || isCompactViewport

    // Generate dynamic button label based on selections
    const getButtonLabel = () => {
      const hasRepos = selectedRepos.length > 0
      const hasUploads = uploadedRepos.length > 0

      if (!hasRepos && !hasUploads) {
        return 'Select Repositories...'
      }

      // Build label parts
      const parts: string[] = []

      if (hasRepos) {
        if (selectedRepos.length === 1) {
          const repoBranch = selectedRepos[0]
          parts.push(`${extractFileDisplayName(repoBranch.repository)} • ${repoBranch.branch}`)
        } else {
          parts.push(`${selectedRepos.length} repos`)
        }
      }

      if (hasUploads) {
        if (uploadedRepos.length === 1) {
          parts.push(uploadedRepos[0].name)
        } else {
          parts.push(`${uploadedRepos.length} uploads`)
        }
      }

      const displayText = parts.join(' + ')
      return displayText.length > 30 ? `${displayText.substring(0, 30)}...` : displayText
    }

    const handleClearSelection = (e: React.MouseEvent) => {
      e.stopPropagation()
      setSelectedRepos([])
      setUploadedRepos([])
    }

    const hasSelection = selectedRepos.length > 0 || uploadedRepos.length > 0
    const showTriggerButton = !landingToolbarLayout || hasSelection

    const modalClassName = isPlugin
      ? 'w-full max-w-[calc(100vw-12px)] h-[calc(100vh-12px)]'
      : usePluginOptimizedLayout
        ? 'w-full max-w-[calc(100vw-24px)] h-[calc(100vh-32px)]'
        : 'w-[640px] h-[629px]'

    return (
      <>
        {showTriggerButton && (
          <button
            type='button'
            {...rest}
            onClick={event => {
              onClick?.(event)
              if (!event.defaultPrevented) {
                openModal()
              }
            }}
            data-testid='repo-select-modal-open-button'
            className={`flex items-center justify-between gap-2 rounded-2xl cursor-pointer transition-colors select-none px-3 py-2 ${
              landingToolbarLayout ? 'min-w-0 flex-1 max-w-[min(280px,100%)]' : 'w-full'
            } ${
              hasSelection
                ? 'bg-tokens-primary-3 hover:bg-tokens-primary-3'
                : 'bg-tokens-neutral-bg-white border border-tokens-neutral-border-2 hover:border-tokens-neutral-border-1'
            } ${className}`}
          >
            <Typography
              fontFamily='geist'
              fontSize='bodySm'
              fontWeight='medium'
              data-testid='repo-select-trigger-label'
              className={`flex-1 text-left overflow-hidden text-ellipsis whitespace-nowrap leading-[16px] ${
                hasSelection ? 'text-tokens-status-link-3' : 'text-tokens-neutral-text-tertiary'
              }`}
            >
              {getButtonLabel()}
            </Typography>
            {hasSelection ? (
              <button
                type='button'
                onClick={handleClearSelection}
                className='flex items-center justify-center shrink-0 w-[16px] h-[16px] rounded hover:bg-tokens-status-link-3/10 transition-colors select-none'
                aria-label='Clear selection'
              >
                <svg
                  width='16'
                  height='16'
                  viewBox='0 0 16 16'
                  fill='none'
                  xmlns='http://www.w3.org/2000/svg'
                  className='text-tokens-status-link-3'
                >
                  <path
                    d='M12 4L4 12M4 4L12 12'
                    stroke='currentColor'
                    strokeWidth='1.5'
                    strokeLinecap='round'
                    strokeLinejoin='round'
                  />
                </svg>
              </button>
            ) : (
              <span className='shrink-0 text-tokens-neutral-text-tertiary' aria-hidden>
                <ChevronDownIcon />
              </span>
            )}
          </button>
        )}

        <RepoSelectModal
          isOpen={isModalOpen}
          onClose={closeModal}
          title='Add Repository'
          commandType={CommandType.MEGA_QA_AGENT_V1}
          workspaceUuid={workspaceUuid}
          initialRepos={selectedRepos}
          initialUploads={uploadedRepos}
          // The chatbox has no separate Confirm step — every change in the
          // modal must reflect on the toolbar pill immediately. `liveBind`
          // pipes selection/upload changes back to the parent on every edit
          // so the X-clear, dynamic label, and tabs-hidden state all update
          // live without waiting for a Confirm click.
          liveBind
          onConfirm={(repos, uploads) => {
            setSelectedRepos(repos)
            setUploadedRepos(uploads)
          }}
          showIdeTab
          compact={usePluginOptimizedLayout}
          className={modalClassName}
          overlayClassName={isPlugin ? 'py-2' : undefined}
        />
      </>
    )
  }
)

RepoSelectButton.displayName = 'RepoSelectButton'

// Background with blurred gradient blobs (uses SVG filter for broad WebKit compat)
const Background = () => (
  <svg
    width='1426'
    height='909'
    viewBox='0 0 1426 909'
    fill='none'
    xmlns='http://www.w3.org/2000/svg'
    className='absolute inset-0 w-full h-full'
    preserveAspectRatio='xMidYMid slice'
    style={{ minWidth: '100%', minHeight: '100%' }}
  >
    <defs>
      <filter id='bgBlur' x='-50%' y='-50%' width='200%' height='200%'>
        <feGaussianBlur in='SourceGraphic' stdDeviation='55' />
      </filter>
      <radialGradient
        id='paint0_angular_651_14791'
        cx='0'
        cy='0'
        r='1'
        gradientUnits='userSpaceOnUse'
        gradientTransform='translate(15 -120) rotate(90) scale(300 300)'
      >
        <stop stopColor='#947BFF' />
        <stop offset='0.245' stopColor='#C8A7FF' />
        <stop offset='0.46' stopColor='#F7DEE9' />
        <stop offset='0.665' stopColor='#CBC4F0' />
        <stop offset='1' stopColor='#FFFFFF' />
      </radialGradient>
      <radialGradient
        id='paint1_angular_651_14791'
        cx='0'
        cy='0'
        r='1'
        gradientUnits='userSpaceOnUse'
        gradientTransform='translate(700 -100) rotate(45) scale(800 800)'
      >
        <stop stopColor='#947BFF' />
        <stop offset='0.245' stopColor='#C8A7FF' />
        <stop offset='0.46' stopColor='#F7DEE9' />
        <stop offset='0.665' stopColor='#CBC4F0' />
        <stop offset='1' stopColor='#FFFFFF' />
      </radialGradient>
      <clipPath id='clip0_651_14791'>
        <rect width='1426' height='909' fill='white' />
      </clipPath>
    </defs>
    <g clipPath='url(#clip0_651_14791)'>
      <rect width='1426' height='909' fill='#FCFCFC' />
      <g filter='url(#bgBlur)'>
        <path
          fillRule='evenodd'
          clipRule='evenodd'
          d='M-240.244 -236.892C-212.453 -308.672 -124.682 -326.497 -55.4679 -360.172C5.98564 -390.072 68.0857 -440.008 132.942 -418.462C196.443 -397.367 214.846 -320.764 244.183 -260.624C270.394 -206.894 296.539 -154.081 290.946 -94.5601C284.115 -21.8557 269.1 56.8428 210.287 100.128C147.149 146.596 61.6624 150.444 -13.9815 129.862C-89.3384 109.358 -150.364 57.3072 -191.369 -9.15849C-233.746 -77.8498 -269.385 -161.625 -240.244 -236.892Z'
          fill='url(#paint0_angular_651_14791)'
        />
        <path
          fillRule='evenodd'
          clipRule='evenodd'
          d='M1635.46 -168.793C1610.43 18.998 1340.75 121.013 1146 247.586C973.084 359.967 816.346 519.898 585.394 513.657C359.268 507.546 229.402 340.051 78.9427 218.75C-55.4833 110.375 -188.857 4.11272 -224.957 -139.655C-269.054 -315.266 -291.959 -510.575 -139.861 -652.618C23.4213 -805.106 298.411 -872.696 563.628 -876.108C827.839 -879.508 1074.15 -798.924 1268.4 -670.74C1469.14 -538.263 1661.7 -365.708 1635.46 -168.793Z'
          fill='url(#paint1_angular_651_14791)'
        />
      </g>
      <rect x='-587' y='-212' width='2590' height='1337' fill='white' fillOpacity='0.36' />
    </g>
  </svg>
)

interface LandingPageChatContainerProps {
  selectedRepositoryBranches: RepositoryBranch[]
  setSelectedRepositoryBranches: (repos: RepositoryBranch[]) => void
  uploadedRepos: UploadedRepo[]
  setUploadedRepos: React.Dispatch<React.SetStateAction<UploadedRepo[]>>
  compact?: boolean
  onConversationCreated?: () => void
  chatFocus?: boolean
}

function LandingPageChatContainer({
  selectedRepositoryBranches,
  setSelectedRepositoryBranches,
  uploadedRepos,
  setUploadedRepos,
  compact = false,
  onConversationCreated,
  chatFocus = true
}: LandingPageChatContainerProps) {
  const [inputValue, setInputValue] = useState('')
  const [isSpeechInputDraft, setIsSpeechInputDraft] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const { workspaceUuid } = useParams<{ workspaceUuid: string }>()
  const { addConversation } = useConversationsContext()
  const navigate = useNavigate()

  // Context uploads - files and directories attached via the attach button
  // Using the extracted hook for upload logic
  const {
    contextUploads,
    handleFilesUpload,
    handleFolderUpload: handleContextFolderUpload,
    handleRemoveContextUpload,
    clearUploads,
    getContextUploadData,
    hasPendingUploads
  } = useContextUpload(workspaceUuid)

  const handleSubmit = async (value: string, isSpeechInput = false) => {
    if (!value.trim() || isLoading) return
    if (!workspaceUuid) {
      console.error('[LandingPage] Cannot submit: invalid workspace UUID')
      return
    }

    // Check if any context uploads are still in progress
    if (hasPendingUploads) {
      BetterToast.error('Please wait for all uploads to complete')
      return
    }

    // Check if any context uploads failed
    const failedContextUploads = contextUploads.filter(item => item.uploadStatus === 'error')
    if (failedContextUploads.length > 0) {
      BetterToast.error('Some uploads failed. Please remove them and try again.')
      return
    }

    setIsLoading(true)
    try {
      // Get context_upload data from the hook (already formatted for API)
      const contextUploadData = getContextUploadData()

      // Collect S3 paths from already-uploaded repos and upload only local pending tar files.
      const s3UploadPaths: string[] = []
      const uploadPromises: Array<Promise<string>> = []
      if (uploadedRepos.length > 0) {
        uploadedRepos.forEach(repo => {
          if (repo.s3Path) {
            s3UploadPaths.push(repo.s3Path)
            return
          }

          if (!repo.tarFile) {
            uploadPromises.push(Promise.reject(new Error(`No tar file found for ${repo.name}`)))
            return
          }

          uploadPromises.push(
            new Promise<string>((resolve, reject) => {
              uploadFile(workspaceUuid!, repo.tarFile!, {
                onComplete: (uploadedS3Path: string) => {
                  resolve(uploadedS3Path)
                },
                onError: (error: string) => {
                  reject(new Error(error))
                }
              })
            })
          )
        })
      }
      if (uploadPromises.length > 0) {
        const paths = await Promise.all(uploadPromises)
        s3UploadPaths.push(...paths)
      }

      // Note: context_upload is stored in sessionStorage below, then passed in the message request
      const conversation = await workspaceChatStart(workspaceUuid, {
        mode: 'MEGA_QA_AGENT_V1',
        repository_branches: selectedRepositoryBranches,
        s3_upload_paths: s3UploadPaths.length > 0 ? s3UploadPaths : undefined,
        execution_mode: 'agent',
        verification_mode: 'verify'
      })

      if (!conversation?.uuid) {
        throw new Error('Failed to create conversation')
      }

      // Store context_upload in sessionStorage for the conversation view to read
      if (contextUploadData.length > 0) {
        sessionStorage.setItem(
          `context_upload_${conversation.uuid}`,
          JSON.stringify(contextUploadData)
        )
      }

      addConversation(conversation)
      onConversationCreated?.()
      const speechInputParam = isSpeechInput ? '&initial_message_is_speech_input=true' : ''
      navigate(
        `/w/${workspaceUuid}/c/${conversation.uuid}?initial_message=${encodeURIComponent(value)}${speechInputParam}`
      )
      setSelectedRepositoryBranches([])
      setUploadedRepos([])
      clearUploads()
      setInputValue('')
      setIsSpeechInputDraft(false)
    } catch (error) {
      console.error('[LandingPage] Failed to initialize conversation:', error)
      const errorMessage = error instanceof Error ? error.message : ''
      const errorType = (error as Error & { errorType?: string })?.errorType

      // These error types are handled by ModalContext via ApiForbiddenError event
      const isHandledByModalContext =
        errorType === 'workspace_read_only' ||
        errorType === 'upgrade_required' ||
        errorType === 'quota_exceeded_overage_disabled' ||
        errorType === 'billable_flows_exceeded_overage_disabled' ||
        errorMessage.includes('credit') ||
        errorMessage.includes('Upgrade') ||
        errorMessage.includes('upgrade') ||
        errorMessage.includes('Billable flow')

      // Only show alert for errors not handled elsewhere
      if (!isHandledByModalContext) {
        alert('Failed to start conversation. Please try again.')
      }
    } finally {
      setIsLoading(false)
    }
  }

  const animatedPlaceholder = useTypewriterPlaceholder({
    placeholders: CHAT_PLACEHOLDERS
  })

  const repoSelectRef = useRef<RepoSelectButtonHandle>(null)

  const handleChatBoxSend = async (value: string) => {
    if (!value.trim()) return
    try {
      await handleSubmit(value, isSpeechInputDraft)
      setInputValue('')
      setIsSpeechInputDraft(false)
    } catch (error) {
      console.error('Failed to submit:', error)
    }
  }

  const handleInputChange = (value: string) => {
    setInputValue(value)
    if (!value.trim()) {
      setIsSpeechInputDraft(false)
    }
  }

  const handleTranscribeAudio = async (audio: Blob) => {
    if (!workspaceUuid) {
      throw new Error('No workspace selected')
    }
    return speechToText(workspaceUuid, audio)
  }

  return (
    <div data-testid='chatbox'>
      <ChatBox
        value={inputValue}
        onChange={handleInputChange}
        onSend={handleChatBoxSend}
        placeholder='Ask a question...'
        animatedPlaceholder={animatedPlaceholder}
        disabled={isLoading}
        multiline={true}
        autoFocus={chatFocus}
        topContent={
          contextUploads.length > 0 ? (
            <UploadCardsContainer uploads={contextUploads} onRemove={handleRemoveContextUpload} />
          ) : undefined
        }
        onFilesSelect={handleFilesUpload}
        onFolderSelect={handleContextFolderUpload}
        currentUploadCount={contextUploads.length}
        repoSelectLeadingSlot={
          <LandingChatPlusMenu
            onAddRepositories={() => repoSelectRef.current?.openModal()}
            onFilesSelect={handleFilesUpload}
            onFolderSelect={handleContextFolderUpload}
            disabled={isLoading}
            currentUploadCount={contextUploads.length}
          />
        }
        repoSelectSlot={
          <RepoSelectButton
            ref={repoSelectRef}
            landingToolbarLayout
            selectedRepos={selectedRepositoryBranches}
            setSelectedRepos={setSelectedRepositoryBranches}
            uploadedRepos={uploadedRepos}
            setUploadedRepos={setUploadedRepos}
          />
        }
        showModeControls={false}
        isSendDisabled={hasPendingUploads}
        compact={compact}
        bookmarksPopoverAlignment='landing'
        onTranscribeAudio={workspaceUuid ? handleTranscribeAudio : undefined}
        onSpeechTranscriptApplied={() => setIsSpeechInputDraft(true)}
        speechButtonTone='light'
        slashCommands={CHAT_SLASH_COMMANDS}
      />
    </div>
  )
}

const CHAT_PLACEHOLDERS = [
  'Transform complex XML payloads into JSON using DataWeave...',
  'Debug why the HTTP Request connector is timing out...',
  'Generate a RAML specification for a Customer System API...',
  'Create an MUnit test suite for this flow...',
  'Improve the test coverage for my project to 80%...',
  'Map Salesforce Account fields to a SAP Customer format...',
  'Draw a sequence diagram for this order processing flow...',
  'Fix the null payload issue after the Scatter-Gather route...',
  'Scaffold a new Mule 4 project with error handling...',
  'Write a script to filter null values from arrays...',
  'Convert this Open API spec into a RAML fragment...',
  'Resolve the duplicate message error in my flow...',
  'Validate this API spec against MuleSoft naming conventions...'
]

// New Testing Icons
const MUnitTestIcon = () => (
  <svg width='24' height='24' viewBox='0 0 24 24' fill='none' xmlns='http://www.w3.org/2000/svg'>
    <path
      d='M20.7835 18.7284L15 9.08625V3.75H15.75C15.9489 3.75 16.1397 3.67098 16.2804 3.53033C16.421 3.38968 16.5 3.19891 16.5 3C16.5 2.80109 16.421 2.61032 16.2804 2.46967C16.1397 2.32902 15.9489 2.25 15.75 2.25H8.25002C8.05111 2.25 7.86034 2.32902 7.71969 2.46967C7.57904 2.61032 7.50002 2.80109 7.50002 3C7.50002 3.19891 7.57904 3.38968 7.71969 3.53033C7.86034 3.67098 8.05111 3.75 8.25002 3.75H9.00002V9.08625L3.21658 18.7284C3.08018 18.9558 3.0065 19.2154 3.00306 19.4805C2.99961 19.7457 3.06652 20.007 3.19697 20.2379C3.32741 20.4688 3.51673 20.6609 3.74563 20.7948C3.97454 20.9287 4.23484 20.9995 4.50002 21H19.5C19.7654 21 20.0261 20.9295 20.2554 20.7959C20.4847 20.6622 20.6744 20.4701 20.8052 20.2391C20.936 20.0081 21.0031 19.7466 20.9998 19.4812C20.9965 19.2158 20.9228 18.9561 20.7863 18.7284H20.7835ZM10.3931 9.67969C10.4633 9.56342 10.5003 9.43016 10.5 9.29437V3.75H13.5V9.29437C13.4998 9.43016 13.5368 9.56342 13.6069 9.67969L17.19 15.6562C16.065 15.8784 14.4647 15.7847 12.3385 14.7084C10.8469 13.9537 9.42752 13.5534 8.09908 13.5075L10.3931 9.67969ZM4.50002 19.5L7.17565 15.0394C8.51158 14.8763 10.0172 15.2128 11.6588 16.0444C13.44 16.9453 14.94 17.2519 16.1588 17.2519C16.7786 17.2546 17.3957 17.1694 17.9916 16.9988L19.5 19.5H4.50002Z'
      fill='#8E8E8E'
    />
  </svg>
)

const SampleDataGeneratorIcon = () => (
  <svg width='24' height='24' viewBox='0 0 24 24' fill='none' xmlns='http://www.w3.org/2000/svg'>
    <path
      d='M4 6V12C4 12 4 15 11 15C11.5925 15 12.1349 14.9785 12.6313 14.9392'
      stroke='#8E8E8E'
      strokeWidth='1.5'
      strokeLinecap='round'
      strokeLinejoin='round'
    />
    <path
      d='M18 6V12'
      stroke='#8E8E8E'
      strokeWidth='1.5'
      strokeLinecap='round'
      strokeLinejoin='round'
    />
    <path
      d='M11 3C18 3 18 6 18 6C18 6 18 9 11 9C4 9 4 6 4 6C4 6 4 3 11 3Z'
      stroke='#8E8E8E'
      strokeWidth='1.5'
      strokeLinecap='round'
      strokeLinejoin='round'
    />
    <path
      d='M11 21C4 21 4 18 4 18V12'
      stroke='#8E8E8E'
      strokeWidth='1.5'
      strokeLinecap='round'
      strokeLinejoin='round'
    />
    <path
      d='M22.6664 17.6667C22.0476 16.097 20.6345 15 18.9901 15C17.2318 15 15.7377 16.2545 15.1968 18'
      stroke='#8E8E8E'
      strokeWidth='1.5'
      strokeLinecap='round'
      strokeLinejoin='round'
    />
    <path
      d='M20.9951 17.6667H22.6664C22.8507 17.6667 23.0001 17.5173 23.0001 17.333V15.4445'
      stroke='#8E8E8E'
      strokeWidth='1.5'
      strokeLinecap='round'
      strokeLinejoin='round'
    />
    <path
      d='M15.3336 20.3333C15.9524 21.903 17.3655 23 19.0099 23C20.7682 23 22.2623 21.7455 22.8032 20'
      stroke='#8E8E8E'
      strokeWidth='1.5'
      strokeLinecap='round'
      strokeLinejoin='round'
    />
    <path
      d='M17.0049 20.3333H15.3336C15.1493 20.3333 14.9999 20.4827 14.9999 20.667V22.5555'
      stroke='#8E8E8E'
      strokeWidth='1.5'
      strokeLinecap='round'
      strokeLinejoin='round'
    />
  </svg>
)

const IntegrationTestIcon = () => (
  <svg width='24' height='24' viewBox='0 0 24 24' fill='none' xmlns='http://www.w3.org/2000/svg'>
    <path
      d='M12 2L2 7V17L12 22L22 17V7L12 2Z'
      stroke='#8E8E8E'
      strokeWidth='1.5'
      strokeLinecap='round'
      strokeLinejoin='round'
    />
    <path
      d='M12 8C13.6569 8 15 6.65685 15 5C15 3.34315 13.6569 2 12 2C10.3431 2 9 3.34315 9 5C9 6.65685 10.3431 8 12 8Z'
      fill='#8E8E8E'
      fillOpacity='0.33'
    />
    <path
      d='M2 7L12 12L22 7'
      stroke='#8E8E8E'
      strokeWidth='1.5'
      strokeLinecap='round'
      strokeLinejoin='round'
    />
    <path
      d='M12 22V12'
      stroke='#8E8E8E'
      strokeWidth='1.5'
      strokeLinecap='round'
      strokeLinejoin='round'
    />
    <circle cx='8' cy='16' r='1.5' fill='#8E8E8E' />
    <circle cx='16' cy='16' r='1.5' fill='#8E8E8E' />
    <path d='M8 14.5L12 12' stroke='#8E8E8E' strokeWidth='1' strokeLinecap='round' />
    <path d='M16 14.5L12 12' stroke='#8E8E8E' strokeWidth='1' strokeLinecap='round' />
  </svg>
)

// New Document Icons
const DocumentGeneratorIcon = () => (
  <svg width='24' height='24' viewBox='0 0 24 24' fill='none' xmlns='http://www.w3.org/2000/svg'>
    <path
      d='M17.0306 13.7194C17.1004 13.789 17.1557 13.8717 17.1934 13.9628C17.2312 14.0538 17.2506 14.1514 17.2506 14.25C17.2506 14.3486 17.2312 14.4462 17.1934 14.5372C17.1557 14.6283 17.1004 14.711 17.0306 14.7806L14.7806 17.0306C14.6399 17.1714 14.449 17.2504 14.25 17.2504C14.051 17.2504 13.8601 17.1714 13.7194 17.0306C13.5786 16.8899 13.4996 16.699 13.4996 16.5C13.4996 16.301 13.5786 16.1101 13.7194 15.9694L15.4397 14.25L13.7194 12.5306C13.5786 12.3899 13.4996 12.199 13.4996 12C13.4996 11.801 13.5786 11.6101 13.7194 11.4694C13.8601 11.3286 14.051 11.2496 14.25 11.2496C14.449 11.2496 14.6399 11.3286 14.7806 11.4694L17.0306 13.7194ZM10.2806 11.4694C10.211 11.3996 10.1283 11.3443 10.0372 11.3066C9.94616 11.2688 9.84856 11.2494 9.75 11.2494C9.65144 11.2494 9.55384 11.2688 9.46279 11.3066C9.37175 11.3443 9.28903 11.3996 9.21937 11.4694L6.96937 13.7194C6.89964 13.789 6.84432 13.8717 6.80658 13.9628C6.76884 14.0538 6.74941 14.1514 6.74941 14.25C6.74941 14.3486 6.76884 14.4462 6.80658 14.5372C6.84432 14.6283 6.89964 14.711 6.96937 14.7806L9.21937 17.0306C9.28906 17.1003 9.37178 17.1556 9.46283 17.1933C9.55387 17.231 9.65145 17.2504 9.75 17.2504C9.84855 17.2504 9.94613 17.231 10.0372 17.1933C10.1282 17.1556 10.2109 17.1003 10.2806 17.0306C10.3503 16.9609 10.4056 16.8782 10.4433 16.7872C10.481 16.6961 10.5004 16.5985 10.5004 16.5C10.5004 16.4015 10.481 16.3039 10.4433 16.2128C10.4056 16.1218 10.3503 16.0391 10.2806 15.9694L8.56031 14.25L10.2806 12.5306C10.3504 12.461 10.4057 12.3783 10.4434 12.2872C10.4812 12.1962 10.5006 12.0986 10.5006 12C10.5006 11.9014 10.4812 11.8038 10.4434 11.7128C10.4057 11.6217 10.3504 11.539 10.2806 11.4694ZM20.25 8.25V20.25C20.25 20.6478 20.092 21.0294 19.8107 21.3107C19.5294 21.592 19.1478 21.75 18.75 21.75H5.25C4.85218 21.75 4.47064 21.592 4.18934 21.3107C3.90804 21.0294 3.75 20.6478 3.75 20.25V3.75C3.75 3.35218 3.90804 2.97064 4.18934 2.68934C4.47064 2.40804 4.85218 2.25 5.25 2.25H14.25C14.3485 2.24992 14.4461 2.26926 14.5371 2.3069C14.6282 2.34454 14.7109 2.39975 14.7806 2.46938L20.0306 7.71938C20.1003 7.78908 20.1555 7.87182 20.1931 7.96286C20.2307 8.05391 20.2501 8.15148 20.25 8.25ZM15 7.5H17.6897L15 4.81031V7.5ZM18.75 20.25V9H14.25C14.0511 9 13.8603 8.92098 13.7197 8.78033C13.579 8.63968 13.5 8.44891 13.5 8.25V3.75H5.25V20.25H18.75Z'
      fill='black'
      fillOpacity='0.45'
    />
  </svg>
)

const DiagramGeneratorIcon = () => (
  <svg width='24' height='24' viewBox='0 0 24 24' fill='none' xmlns='http://www.w3.org/2000/svg'>
    <path
      d='M15 10.5H19.5C19.8978 10.5 20.2794 10.342 20.5607 10.0607C20.842 9.77936 21 9.39782 21 9V4.5C21 4.10218 20.842 3.72064 20.5607 3.43934C20.2794 3.15804 19.8978 3 19.5 3H15C14.6022 3 14.2206 3.15804 13.9393 3.43934C13.658 3.72064 13.5 4.10218 13.5 4.5V6H12C11.4033 6 10.831 6.23705 10.409 6.65901C9.98705 7.08097 9.75 7.65326 9.75 8.25V11.25H6.75V10.5C6.75 10.1022 6.59196 9.72064 6.31066 9.43934C6.02936 9.15804 5.64782 9 5.25 9H2.25C1.85218 9 1.47064 9.15804 1.18934 9.43934C0.908035 9.72064 0.75 10.1022 0.75 10.5V13.5C0.75 13.8978 0.908035 14.2794 1.18934 14.5607C1.47064 14.842 1.85218 15 2.25 15H5.25C5.64782 15 6.02936 14.842 6.31066 14.5607C6.59196 14.2794 6.75 13.8978 6.75 13.5V12.75H9.75V15.75C9.75 16.3467 9.98705 16.919 10.409 17.341C10.831 17.7629 11.4033 18 12 18H13.5V19.5C13.5 19.8978 13.658 20.2794 13.9393 20.5607C14.2206 20.842 14.6022 21 15 21H19.5C19.8978 21 20.2794 20.842 20.5607 20.5607C20.842 20.2794 21 19.8978 21 19.5V15C21 14.6022 20.842 14.2206 20.5607 13.9393C20.2794 13.658 19.8978 13.5 19.5 13.5H15C14.6022 13.5 14.2206 13.658 13.9393 13.9393C13.658 14.2206 13.5 14.6022 13.5 15V16.5H12C11.8011 16.5 11.6103 16.421 11.4697 16.2803C11.329 16.1397 11.25 15.9489 11.25 15.75V8.25C11.25 8.05109 11.329 7.86032 11.4697 7.71967C11.6103 7.57902 11.8011 7.5 12 7.5H13.5V9C13.5 9.39782 13.658 9.77936 13.9393 10.0607C14.2206 10.342 14.6022 10.5 15 10.5ZM5.25 13.5H2.25V10.5H5.25V13.5ZM15 15H19.5V19.5H15V15ZM15 4.5H19.5V9H15V4.5Z'
      fill='black'
      fillOpacity='0.45'
    />
  </svg>
)

const DomainDiagramGeneratorIcon = () => (
  <svg width='24' height='24' viewBox='0 0 24 24' fill='none' xmlns='http://www.w3.org/2000/svg'>
    <path
      d='M15 10.5H19.5C19.8978 10.5 20.2794 10.342 20.5607 10.0607C20.842 9.77936 21 9.39782 21 9V4.5C21 4.10218 20.842 3.72064 20.5607 3.43934C20.2794 3.15804 19.8978 3 19.5 3H15C14.6022 3 14.2206 3.15804 13.9393 3.43934C13.658 3.72064 13.5 4.10218 13.5 4.5V6H12C11.4033 6 10.831 6.23705 10.409 6.65901C9.98705 7.08097 9.75 7.65326 9.75 8.25V11.25H6.75V10.5C6.75 10.1022 6.59196 9.72064 6.31066 9.43934C6.02936 9.15804 5.64782 9 5.25 9H2.25C1.85218 9 1.47064 9.15804 1.18934 9.43934C0.908035 9.72064 0.75 10.1022 0.75 10.5V13.5C0.75 13.8978 0.908035 14.2794 1.18934 14.5607C1.47064 14.842 1.85218 15 2.25 15H5.25C5.64782 15 6.02936 14.842 6.31066 14.5607C6.59196 14.2794 6.75 13.8978 6.75 13.5V12.75H9.75V15.75C9.75 16.3467 9.98705 16.919 10.409 17.341C10.831 17.7629 11.4033 18 12 18H13.5V19.5C13.5 19.8978 13.658 20.2794 13.9393 20.5607C14.2206 20.842 14.6022 21 15 21H19.5C19.8978 21 20.2794 20.842 20.5607 20.5607C20.842 20.2794 21 19.8978 21 19.5V15C21 14.6022 20.842 14.2206 20.5607 13.9393C20.2794 13.658 19.8978 13.5 19.5 13.5H15C14.6022 13.5 14.2206 13.658 13.9393 13.9393C13.658 14.2206 13.5 14.6022 13.5 15V16.5H12C11.8011 16.5 11.6103 16.421 11.4697 16.2803C11.329 16.1397 11.25 15.9489 11.25 15.75V8.25C11.25 8.05109 11.329 7.86032 11.4697 7.71967C11.6103 7.57902 11.8011 7.5 12 7.5H13.5V9C13.5 9.39782 13.658 9.77936 13.9393 10.0607C14.2206 10.342 14.6022 10.5 15 10.5ZM5.25 13.5H2.25V10.5H5.25V13.5ZM15 15H19.5V19.5H15V15ZM15 4.5H19.5V9H15V4.5Z'
      fill='black'
      fillOpacity='0.45'
    />
  </svg>
)

const MappingTableGeneratorIcon = () => (
  <svg width='24' height='24' viewBox='0 0 24 24' fill='none' xmlns='http://www.w3.org/2000/svg'>
    <g clipPath='url(#clip0_764_2131)'>
      <path
        d='M18 4H6C4.89543 4 4 4.89543 4 6V18C4 19.1046 4.89543 20 6 20H18C19.1046 20 20 19.1046 20 18V6C20 4.89543 19.1046 4 18 4Z'
        stroke='black'
        strokeOpacity='0.45'
        strokeWidth='2'
        strokeLinecap='round'
        strokeLinejoin='round'
      />
      <path
        d='M4 10H20'
        stroke='black'
        strokeOpacity='0.45'
        strokeWidth='2'
        strokeLinecap='round'
        strokeLinejoin='round'
      />
      <path
        d='M10 4V20'
        stroke='black'
        strokeOpacity='0.45'
        strokeWidth='2'
        strokeLinecap='round'
        strokeLinejoin='round'
      />
    </g>
    <defs>
      <clipPath id='clip0_764_2131'>
        <rect width='24' height='24' fill='white' />
      </clipPath>
    </defs>
  </svg>
)

type SkillCategory = 'code' | 'test' | 'document' | 'migration'

export const LandingPage: React.FC<{
  onConversationCreated?: () => void
  chatFocus?: boolean
  setChatFocus?: (value: boolean) => void
}> = ({ onConversationCreated, chatFocus = true }) => {
  const navigate = useNavigate()
  const dispatch = useDispatch()
  const isPlugin = bridge.isInPlugin()
  const isCompactViewport = useCompactViewport()

  // Get integration test flow state from Redux
  const currentStep = useSelector(selectCurrentStep)
  const flowRepository = useSelector(selectFlowRepository)
  const integrationTestFlow = useSelector(selectIntegrationTestFlow)

  // Repository and upload state (lifted from LandingPageChatContainer)
  const [selectedRepositoryBranches, setSelectedRepositoryBranches] = useState<RepositoryBranch[]>(
    []
  )
  const [uploadedRepos, setUploadedRepos] = useState<UploadedRepo[]>([])

  const [showSkills, setShowSkills] = useState(false)
  const [skillsPosition, setSkillsPosition] = useState({ x: 0, y: 0, width: 0 })
  const [selectedCategory, setSelectedCategory] = useState<SkillCategory>('code')
  // Initialize modal as open if we're at selectRepository step - prevents animation
  const [isIntegrationRepoModalOpen, setIntegrationRepoModalOpen] = useState(
    currentStep === 'selectRepository'
  )
  const tabContainerRef = React.useRef<HTMLDivElement>(null)
  const skillsContainerRef = useRef<HTMLDivElement>(null)

  // Billing logic moved from SkillsView for generic reuse
  const { workspaceUuid } = useParams<{ workspaceUuid: string }>()
  const { isCommandEnabled } = useCommandPermissions()
  const { userConfig } = useUserSpecificConfig()
  // Azure Migrate + Deploy is experimental (simulated deploy) — keep it
  // super-admin-only for now until the real deploy path lands.
  const isSuperAdmin = userConfig?.is_super_admin || false

  // Generic function to get skills for any category
  const getCategorySkills = (category: SkillCategory): Skill[] => {
    switch (category) {
      case 'code':
        return [
          {
            icon: <CoderIcon />,
            label: 'Coder',
            description: 'Generate MuleSoft code from specifications',
            commandType: CommandType.GENERATE_SIMPLE_CODE_PR
          },
          {
            icon: <DataWeaveIcon />,
            label: 'DataWeave Generator',
            description: 'Generate DataWeave code from input/output samples',
            commandType: CommandType.INPUT_OUTPUT_EXAMPLE_TO_DWL_GENERATION
          }
        ]
      case 'test':
        return [
          {
            icon: <MUnitTestIcon />,
            label: 'MUnit Test Generator',
            description: 'Generate complete MUnit tests for MuleSoft flows',
            commandType: CommandType.MUNIT_TEST_CREATION
          },
          {
            icon: <SampleDataGeneratorIcon />,
            label: 'Sample Test Data Generator',
            description: 'Generate MUnit test data',
            commandType: CommandType.DATAWEAVE_TO_DATA_CREATION
          },
          {
            icon: <IntegrationTestIcon />,
            label: 'Integration Test Generator',
            description: 'Generate comprehensive integration tests for MuleSoft flows',
            commandType: CommandType.INTEGRATION_TEST,
            visible: isCommandEnabled(CommandType.INTEGRATION_TEST)
          }
        ]
      case 'document':
        return [
          {
            icon: <DocumentGeneratorIcon />,
            label: 'Document Generator',
            description: 'Generate comprehensive documentation for MuleSoft flows',
            commandType: CommandType.GENERATE_DOCUMENTATION
          },
          {
            icon: <DiagramGeneratorIcon />,
            label: 'Diagram Generator',
            description: 'Generate Sequence and Flow diagrams',
            commandType: CommandType.GENERATE_IMAGE
          },
          {
            icon: <DomainDiagramGeneratorIcon />,
            label: 'Domain Diagram Generator',
            description: 'Generate diagrams mapping API dependencies across repositories',
            commandType: CommandType.GENERATE_MULTI_REPO_FLOW_DIAGRAM
          },
          {
            icon: <MappingTableGeneratorIcon />,
            label: 'Mapping Table Generator',
            description: 'Generate mapping table from Dataweave script',
            commandType: CommandType.GENERATE_MAPPING_TABLE
          }
        ]
      case 'migration':
        return [
          {
            icon: <MigrationGeneratorIcon />,
            label: 'Migration Generator',
            description: 'Generates migration documents',
            commandType: CommandType.MIGRATION_DOCUMENT,
            visible: isCommandEnabled(CommandType.MIGRATION_DOCUMENT)
          },
          {
            icon: <BillableMuleFlowsIcon />,
            label: 'Migration Inventory',
            description: 'Generate API inventory for Migrations',
            commandType: CommandType.BILLABLE_FLOW_DOCUMENTATION,
            visible: isCommandEnabled(CommandType.BILLABLE_FLOW_DOCUMENTATION)
          },
          {
            icon: <MigrationAcceleratorIcon />,
            label: 'Migration Accelerator',
            description: 'Accelerate migration with comprehensive analysis',
            commandType: CommandType.MIGRATION_ACCELERATOR,
            visible: isCommandEnabled(CommandType.MIGRATION_ACCELERATOR)
          },
          {
            icon: <MuleRefactorAssistantIcon />,
            label: 'Mule Refactor Assistant',
            description: 'Refactor legacy Mule integrations into API-led architecture',
            commandType: CommandType.MULE_REFACTORING,
            visible: isCommandEnabled(CommandType.MULE_REFACTORING)
          },
          {
            icon: <MuleVersionUpgradeIcon />,
            label: 'Mule Version Upgrade',
            description: 'Upgrade Mule applications to newer runtime versions',
            commandType: CommandType.MULE_VERSION_UPGRADE,
            visible: isCommandEnabled(CommandType.MULE_VERSION_UPGRADE)
          },
          {
            icon: <SpringIntegrationRefactorIcon />,
            label: 'Spring Integration Refactor',
            description:
              'Refactor an existing integration repository into Spring Boot 3.x + Spring Integration',
            commandType: CommandType.SPRING_INTEGRATION_REFACTORING,
            visible: isCommandEnabled(CommandType.SPRING_INTEGRATION_REFACTORING)
          },
          {
            icon: <AzureIcon />,
            label: 'Azure Migrate + Deploy',
            description: 'Migrate to Azure and deploy (deploy currently simulated)',
            commandType: CommandType.AZURE_DEPLOY_TEST,
            visible: isSuperAdmin && isCommandEnabled(CommandType.AZURE_DEPLOY_TEST),
            beta: true
          }
        ]
      default:
        return []
    }
  }

  // Generic function to check if any category has visible skills
  const hasVisibleSkills = (category: SkillCategory) => {
    return getCategorySkills(category).filter(skill => skill.visible !== false).length > 0
  }

  // Map skills to their corresponding CommandTypes
  const getSkillCommandType = (skillLabel: string): CommandType | null => {
    switch (skillLabel) {
      case 'Coder':
        return CommandType.GENERATE_SIMPLE_CODE_PR
      case 'DataWeave Generator':
        return CommandType.INPUT_OUTPUT_EXAMPLE_TO_DWL_GENERATION
      case 'MUnit Test Generator':
        return CommandType.MUNIT_TEST_CREATION
      case 'Sample Test Data Generator':
        return CommandType.DATAWEAVE_TO_DATA_CREATION
      case 'Integration Test Generator':
        return CommandType.INTEGRATION_TEST
      case 'Document Generator':
        return CommandType.GENERATE_DOCUMENTATION
      case 'Diagram Generator':
        return CommandType.GENERATE_IMAGE
      case 'Domain Diagram Generator':
        return CommandType.GENERATE_MULTI_REPO_FLOW_DIAGRAM
      case 'Mapping Table Generator':
        return CommandType.GENERATE_MAPPING_TABLE
      case 'Migration Generator':
        return CommandType.MIGRATION_DOCUMENT
      case 'Migration Inventory':
        return CommandType.BILLABLE_FLOW_DOCUMENTATION
      case 'Migration Accelerator':
        return CommandType.MIGRATION_ACCELERATOR
      case 'Mule Refactor Assistant':
        return CommandType.MULE_REFACTORING
      case 'Mule Version Upgrade':
        return CommandType.MULE_VERSION_UPGRADE
      case 'Spring Integration Refactor':
        return CommandType.SPRING_INTEGRATION_REFACTORING
      case 'Azure Migrate + Deploy':
        return CommandType.AZURE_DEPLOY_TEST
      default:
        return null
    }
  }

  // Navigate to workspace with command parameter
  const handleSkillClick = async (skillLabel: string) => {
    const commandType = getSkillCommandType(skillLabel)
    if (!commandType) return

    // Special handling for Integration Test Generator - show modal instead of navigating
    if (skillLabel === 'Integration Test Generator') {
      dispatch(startFlow())
      setIntegrationRepoModalOpen(true)
      return
    }

    const commandUrlName = getCommandUrlName(commandType)
    if (!commandUrlName) return

    // Preserve existing URL params (like s3_tar_file_path from plugin uploads)
    const searchParams = new URLSearchParams(window.location.search)
    searchParams.set('command', commandUrlName)
    searchParams.set('withExamplePrefilled', 'false')
    navigate(`/w/${workspaceUuid}?${searchParams.toString()}`)
  }

  const handleShowSkills = (category: SkillCategory) => {
    if (tabContainerRef.current) {
      const tabRect = tabContainerRef.current.getBoundingClientRect()
      setSkillsPosition({
        x: 0,
        y: tabRect.top,
        width: 0
      })
    }
    setSelectedCategory(category)
    setShowSkills(true)
  }

  // Handle integration test repo selection modal save
  const handleIntegrationRepoSave = (data: {
    option: 'none' | 'upload' | 'codeContext' | 'mappingTable'
    s3Path?: string
    repository?: { conn_type: string; repository: string; branch: string }
    runtimeSettings?: Record<string, string | undefined>
  }) => {
    // If codeContext option with repository, dispatch Redux action to start flow
    if (data.option === 'codeContext' && data.repository) {
      dispatch(
        completeRepositorySelection({
          repository: data.repository,
          runtimeSettings: data.runtimeSettings
        })
      )
      setIntegrationRepoModalOpen(false)
    }

    // If upload option with s3Path, dispatch Redux action with S3 upload
    if (data.option === 'upload' && data.s3Path) {
      dispatch(
        completeRepositorySelection({
          repository: {
            conn_type: 'S3',
            repository: '',
            branch: 'main',
            s3TarFilePath: data.s3Path
          },
          runtimeSettings: data.runtimeSettings || {}
        })
      )
      setIntegrationRepoModalOpen(false)
    }

    // Navigate to the Integration Test task form
    const searchParams = new URLSearchParams()
    searchParams.set('command', 'integration-test')
    searchParams.set('withExamplePrefilled', 'false')

    // Navigate to workspace home with command parameters (consistent with other skills)
    navigate(`/w/${workspaceUuid}?${searchParams.toString()}`)
  }

  // Auto-open integration test repo modal when returning from flow (if not already open)
  useEffect(() => {
    if (currentStep === 'selectRepository' && !isIntegrationRepoModalOpen) {
      setIntegrationRepoModalOpen(true)
    }
  }, [currentStep, isIntegrationRepoModalOpen])

  // Click/touch outside handler to close skills container
  useEffect(() => {
    const handleClickOutside = (event: PointerEvent) => {
      const target = event.target as HTMLElement | null
      if (target && target.closest('[role="dialog"]')) {
        return
      }
      if (
        showSkills &&
        skillsContainerRef.current &&
        !skillsContainerRef.current.contains(event.target as Node) &&
        tabContainerRef.current &&
        !tabContainerRef.current.contains(event.target as Node)
      ) {
        setShowSkills(false)
      }
    }

    if (showSkills) {
      document.addEventListener('pointerdown', handleClickOutside)
    }

    return () => {
      document.removeEventListener('pointerdown', handleClickOutside)
    }
  }, [showSkills])

  const noRepos = selectedRepositoryBranches.length === 0 && uploadedRepos.length === 0

  const integrationRepoModal = (
    <IntegrationRepoSelectionModal
      isOpen={isIntegrationRepoModalOpen}
      onClose={() => {
        setIntegrationRepoModalOpen(false)
        if (currentStep === 'selectRepository') {
          dispatch(resetFlow())
        }
      }}
      command={CommandType.INTEGRATION_TEST}
      initialOption='codeContext'
      repositories={
        flowRepository
          ? [
              {
                conn_type: flowRepository.conn_type,
                repository: flowRepository.repository,
                branch: flowRepository.branch
              }
            ]
          : []
      }
      initialS3Path=''
      taskRuntimeSettings={integrationTestFlow.runtimeSettings}
      onSave={handleIntegrationRepoSave}
      disabled={false}
    />
  )

  // Plugin: desktop-style landing page with decorative background
  if (isPlugin) {
    return (
      <div className='h-full w-full min-h-0 flex flex-col relative'>
        <div className='absolute inset-0 bg-tokens-neutral-bg-grey-1'>
          <div className='absolute inset-0 dark:hidden'>
            <Background />
          </div>
          <div
            className='absolute inset-0 hidden dark:block'
            style={{
              background:
                'radial-gradient(80% 60% at 50% -10%, rgba(123,97,255,0.16) 0%, rgba(24,24,27,0) 60%)'
            }}
          />
        </div>
        <div className='relative z-[1] flex-1 min-h-0 flex flex-col items-center overflow-y-auto overflow-x-hidden px-6 pt-[20vh] pb-6'>
          <div className='w-full max-w-3xl flex flex-col gap-8'>
            <div className='w-full text-center'>
              <Typography
                fontFamily='geist'
                fontSize='displaySm'
                fontWeight='medium'
                style={{
                  background: 'linear-gradient(90deg, #AB97FE 0%, #D3B6B8 82%, #A8CFF1 98.98%)',
                  backgroundClip: 'text',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  userSelect: 'none'
                }}
              >
                How can I help you today?
              </Typography>
            </div>
            <div className='bg-tokens-neutral-bg-white rounded-3xl shadow-sm'>
              <LandingPageChatContainer
                selectedRepositoryBranches={selectedRepositoryBranches}
                setSelectedRepositoryBranches={setSelectedRepositoryBranches}
                uploadedRepos={uploadedRepos}
                setUploadedRepos={setUploadedRepos}
                compact={isCompactViewport}
                onConversationCreated={onConversationCreated}
                chatFocus={chatFocus}
              />
            </div>

            {noRepos && (
              <div ref={tabContainerRef} className='flex flex-wrap gap-3 px-1'>
                {hasVisibleSkills('code') && (
                  <TabItem
                    data-testid='tabitem-code'
                    text='Code'
                    icon={<CodeIcon />}
                    active={showSkills && selectedCategory === 'code'}
                    onClick={() => handleShowSkills('code')}
                  />
                )}
                {hasVisibleSkills('test') && (
                  <TabItem
                    data-testid='tabitem-test'
                    text='Test'
                    icon={<TestIcon />}
                    active={showSkills && selectedCategory === 'test'}
                    onClick={() => handleShowSkills('test')}
                  />
                )}
                {hasVisibleSkills('document') && (
                  <TabItem
                    data-testid='tabitem-document'
                    text='Document'
                    icon={<DocumentIcon />}
                    active={showSkills && selectedCategory === 'document'}
                    onClick={() => handleShowSkills('document')}
                  />
                )}
                {hasVisibleSkills('migration') && (
                  <TabItem
                    data-testid='tabitem-migration'
                    text='Migration'
                    icon={<MigrationIcon />}
                    active={showSkills && selectedCategory === 'migration'}
                    onClick={() => handleShowSkills('migration')}
                  />
                )}
              </div>
            )}

            {showSkills && (
              <div ref={skillsContainerRef} className='w-full'>
                <SkillsView
                  skills={getCategorySkills(selectedCategory).filter(
                    skill => skill.visible !== false
                  )}
                  onSkillClick={handleSkillClick}
                />
              </div>
            )}
          </div>
        </div>

        {integrationRepoModal}
      </div>
    )
  }

  // Web: greeting-first layout with decorative background
  return (
    <div className='min-h-screen w-full flex flex-col items-center justify-center relative'>
      <div className='absolute inset-0 overflow-hidden'>
        <div className='absolute inset-0 bg-tokens-neutral-bg-grey-1 overflow-hidden'>
          <div className='absolute inset-0 dark:hidden'>
            <Background />
          </div>
          <div
            className='absolute inset-0 hidden dark:block'
            style={{
              background:
                'radial-gradient(80% 60% at 50% -10%, rgba(123,97,255,0.16) 0%, rgba(24,24,27,0) 60%)'
            }}
          />
          <div className='absolute inset-0 flex flex-col items-center gap-12 px-4'>
            <div className='w-full max-w-3xl text-center mt-[160px]'>
              <Typography
                fontFamily='geist'
                fontSize='displaySm'
                fontWeight='medium'
                style={{
                  background: 'linear-gradient(90deg, #AB97FE 0%, #D3B6B8 82%, #A8CFF1 98.98%)',
                  backgroundClip: 'text',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  userSelect: 'none'
                }}
              >
                How can I help you today?
              </Typography>
            </div>
            <div className='w-full max-w-3xl flex flex-col gap-6'>
              <div className='bg-tokens-neutral-bg-white rounded-3xl shadow-sm'>
                <LandingPageChatContainer
                  selectedRepositoryBranches={selectedRepositoryBranches}
                  setSelectedRepositoryBranches={setSelectedRepositoryBranches}
                  uploadedRepos={uploadedRepos}
                  setUploadedRepos={setUploadedRepos}
                  onConversationCreated={onConversationCreated}
                  chatFocus={chatFocus}
                />
              </div>

              {noRepos && (
                <div ref={tabContainerRef} className='flex gap-4 px-4 py-2'>
                  {hasVisibleSkills('code') && (
                    <TabItem
                      data-testid='tabitem-code'
                      text='Code'
                      icon={<CodeIcon />}
                      active={showSkills && selectedCategory === 'code'}
                      onClick={() => handleShowSkills('code')}
                    />
                  )}
                  {hasVisibleSkills('test') && (
                    <TabItem
                      data-testid='tabitem-test'
                      text='Test'
                      icon={<TestIcon />}
                      active={showSkills && selectedCategory === 'test'}
                      onClick={() => handleShowSkills('test')}
                    />
                  )}
                  {hasVisibleSkills('document') && (
                    <TabItem
                      data-testid='tabitem-document'
                      text='Document'
                      icon={<DocumentIcon />}
                      active={showSkills && selectedCategory === 'document'}
                      onClick={() => handleShowSkills('document')}
                    />
                  )}
                  {hasVisibleSkills('migration') && (
                    <TabItem
                      data-testid='tabitem-migration'
                      text='Migration'
                      icon={<MigrationIcon />}
                      active={showSkills && selectedCategory === 'migration'}
                      onClick={() => handleShowSkills('migration')}
                    />
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {showSkills && (
        <div
          ref={skillsContainerRef}
          className='absolute z-50 w-full max-w-3xl left-1/2 -translate-x-1/2 translate-y-[64px]'
          style={{ top: skillsPosition.y }}
        >
          <SkillsView
            skills={getCategorySkills(selectedCategory).filter(skill => skill.visible !== false)}
            onSkillClick={handleSkillClick}
          />
        </div>
      )}

      {integrationRepoModal}
    </div>
  )
}

export default LandingPage
